/**
 * Slotting helpers — OutpostForge frontend
 * Bridges API asset data to slotting-core's model: the API exposes MaxVcpus
 * but not memory per asset, so the local catalog complements it (family
 * capacities, generation and NUMA topology).
 */

import {
  INSTANCE_CATALOG,
  calculateTotals,
  computeDropletLayout,
  type Droplet,
  type DropletLayout,
  type FamilyData,
  type ResourceTotals,
} from 'slotting-core';
import type { AssetSummary } from '../types/outposts';

export interface AssetSlotting {
  generation: string;
  familyKey: string;
  familyData: FamilyData;
  droplet: Droplet;
  layout: DropletLayout;
  totals: ResourceTotals;
  /** vCPU capacity from the API (MaxVcpus) falling back to the catalog. */
  vcpuCapacity: number;
}

/** Find the catalog generation and family key for a family name (case-insensitive). */
export function findFamily(
  familyName: string,
): { generation: string; familyKey: string; familyData: FamilyData } | undefined {
  const lower = familyName.toLowerCase();
  for (const [generation, families] of Object.entries(INSTANCE_CATALOG)) {
    for (const [familyKey, familyData] of Object.entries(families)) {
      if (familyKey.toLowerCase() === lower) {
        return { generation, familyKey, familyData };
      }
    }
  }
  return undefined;
}

/**
 * Compute the visual slotting model for an asset from its current
 * configuration. Returns undefined when the family cannot be resolved
 * against the catalog (e.g. an INSTALLING asset with no families).
 */
export function computeAssetSlotting(asset: AssetSummary): AssetSlotting | undefined {
  const familyName = asset.instanceFamilies[0];
  if (!familyName) return undefined;

  const found = findFamily(familyName);
  if (!found) return undefined;

  const droplet: Droplet = {
    family: found.familyKey,
    instances: Object.fromEntries(
      asset.instanceTypeCapacities.map((c) => [c.instanceType, c.count]),
    ),
  };

  const layout = computeDropletLayout(droplet, found.familyData, found.generation);
  const totals = calculateTotals(droplet, found.familyData);

  return {
    generation: found.generation,
    familyKey: found.familyKey,
    familyData: found.familyData,
    droplet,
    layout,
    totals,
    vcpuCapacity: asset.maxVcpus ?? found.familyData.dropletCapacity.vCPUs,
  };
}
